import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useQueryClient } from '@tanstack/react-query';
import { exportUsajiliPdf, PDF_TODATE, PDF_TREND } from '@components/usajili/usajiliPdf';
import { sleep, waitFor } from '@utils/pdfExport';
import { USAJILI_COUNTIES } from '@types';
import {
  Dashboard as DashboardIcon,
  GridOn as GridIcon,
  PivotTableChart as PivotIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { USAJILIThemeProvider, USAJILIColorModeToggle, useUSAJILIColorMode } from '@theme/USAJILIThemeProvider';
import { useNavigate } from 'react-router-dom';
import { useUSAJILI, USAJILIProvider } from '@hooks/useUSAJILI';
import { USAJILIFilterBar } from '@components/usajili/USAJILIFilterBar';
import { USAJILIHeaderStatus } from '@components/usajili/USAJILIHeaderStatus';
import { USAJILIOverviewTab } from '@components/usajili/tabs/USAJILIOverviewTab';
import { USAJILIPivotTab } from '@components/usajili/tabs/USAJILIPivotTab';
import { USAJILIDailyTab } from '@components/usajili/tabs/USAJILIDailyTab';
import { USAJILIBulkUploadDialog } from '@components/usajili/USAJILIBulkUploadDialog';
import { USAJILIAuditLogDialog } from '@components/usajili/USAJILIAuditLogDialog';
import { CircularProgress, Alert } from '@mui/material';

type TabIdx = 0 | 1 | 2;

const TABS: Array<{ label: string; icon: React.ReactElement }> = [
  { label: 'Dashboard', icon: <DashboardIcon sx={{ fontSize: 15 }} /> },
  { label: 'Pivot table', icon: <PivotIcon sx={{ fontSize: 15 }} /> },
  { label: 'Daily data', icon: <GridIcon sx={{ fontSize: 15 }} /> },
];

const Shell: React.FC = () => {
  const muiTheme = useTheme();
  const { chrome } = useUSAJILIColorMode();
  const {
    appbarBg: APPBAR_BG,
    appbarBorder: APPBAR_BORDER,
    textDim: TEXT_DIM,
    textMain: TEXT_MAIN,
    contentBg: CONTENT_BG,
  } = chrome;
  const ACCENT_GREEN = muiTheme.palette.primary.main;
  const ACCENT_AMBER = muiTheme.palette.secondary.main;
  const { loading, error, role, officer, isAdmin, logout, bumpRefetch, metadata, filters, refetchKey } = useUSAJILI();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Phones and portrait tablets: icon-only header actions.
  const compact = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [tabIdx, setTabIdx] = useState<TabIdx>(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [addRequested, setAddRequested] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // The PDF takes its charts from the Dashboard tab, so open it first and
  // let the charts draw (and finish animating) before capturing them.
  const onExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      const switched = tabIdx !== 0;
      if (switched) setTabIdx(0);
      const drawn = (sel: string) => document.querySelector(`${sel} .recharts-wrapper > svg`);
      await waitFor(() => drawn(PDF_TREND) && drawn(PDF_TODATE));
      if (switched) await sleep(1800);
      await exportUsajiliPdf(queryClient, filters, refetchKey, metadata?.counties || USAJILI_COUNTIES, metadata);
    } catch (e: any) {
      setExportError(e?.message || e?.error?.message || 'Could not create the PDF.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const onUp = () => setUploadOpen(true);
    const onAu = () => setAuditOpen(true);
    window.addEventListener('usajili:open-upload', onUp);
    window.addEventListener('usajili:open-audit', onAu);
    return () => {
      window.removeEventListener('usajili:open-upload', onUp);
      window.removeEventListener('usajili:open-audit', onAu);
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center', bgcolor: CONTENT_BG, minHeight: '100vh' }}>
        <CircularProgress sx={{ color: ACCENT_GREEN }} />
        <Typography sx={{ mt: 2, color: TEXT_DIM }}>Loading USAJILI…</Typography>
      </Box>
    );
  }

  if (error || !role) {
    return (
      <Box sx={{ p: 6, maxWidth: 720, mx: 'auto', bgcolor: CONTENT_BG, minHeight: '100vh' }}>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/usajili/login')}>
              Back to Login
            </Button>
          }
        >
          {error || 'No active USAJILI session. Please log in.'}
        </Alert>
      </Box>
    );
  }

  const initials = (officer?.full_name || 'U').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { lg: '100dvh' }, minHeight: '100dvh', bgcolor: CONTENT_BG }}>

      {/* ── AppBar — matches SDICS admin layout ── */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: APPBAR_BG, boxShadow: 'none', borderBottom: `1px solid ${APPBAR_BORDER}`,
          // On phones the header (with filters) is tall, so let it scroll away.
          position: { xs: 'static', sm: 'sticky' },
        }}
      >
        {/* ── Top toolbar: title + actions ── */}
        <Toolbar sx={{ minHeight: 56, py: 0.5, gap: { xs: 0.75, sm: 1.5 }, px: { xs: 1, sm: 2 } }}>
          {/* Logo — Kenya Coat of Arms */}
          <Box
            component="img"
            src="/coat-of-arms-sm.png"
            alt="Coat of Arms"
            sx={{ width: 34, height: 34, display: { xs: 'none', sm: 'block' }, borderRadius: 1.5, bgcolor: '#fff', p: '2px', objectFit: 'contain', flexShrink: 0 }}
          />

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: TEXT_MAIN, lineHeight: 1.2, letterSpacing: 0.5 }}>
              RRI USAJILI
            </Typography>
            <USAJILIHeaderStatus
              dataFrom={metadata?.data_from}
              dataTo={metadata?.data_to}
              countyCount={metadata?.counties?.length}
            />
          </Box>

          {/* Role badge */}
          <Chip
            label={isAdmin ? 'ADMIN' : 'HQ'}
            size="small"
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              height: 22, fontSize: '0.68rem', fontWeight: 700,
              bgcolor: alpha(isAdmin ? ACCENT_AMBER : ACCENT_GREEN, 0.12),
              color: isAdmin ? ACCENT_AMBER : ACCENT_GREEN,
              border: `1px solid ${alpha(isAdmin ? ACCENT_AMBER : ACCENT_GREEN, 0.35)}`,
            }}
          />

          {/* Admin-only action buttons */}
          {isAdmin && (
            <>
              {compact ? (
                <>
                  <Tooltip title="Add row">
                    <IconButton size="small" onClick={() => { setTabIdx(2); setAddRequested(true); }} sx={{ color: ACCENT_GREEN }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Upload Excel">
                    <IconButton size="small" onClick={() => setUploadOpen(true)} sx={{ color: ACCENT_AMBER }}>
                      <CloudUploadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => { setTabIdx(2); setAddRequested(true); }}
                    sx={{ py: 0.5, px: 1.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                  >
                    Add row
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setUploadOpen(true)}
                    sx={{ py: 0.5, px: 1.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                  >
                    Upload Excel
                  </Button>
                </>
              )}
              <Tooltip title="Audit log">
                <IconButton
                  size="small"
                  onClick={() => setAuditOpen(true)}
                  sx={{ color: TEXT_DIM, '&:hover': { color: ACCENT_GREEN } }}
                >
                  <ManageSearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          {compact ? (
            <Tooltip title={filters.county !== 'All' ? `Export PDF · ${filters.county}` : 'Export PDF'}>
              <span>
                <IconButton size="small" onClick={onExport} disabled={exporting} sx={{ color: TEXT_DIM, '&:hover': { color: ACCENT_GREEN } }}>
                  {exporting ? <CircularProgress size={16} /> : <PictureAsPdfIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
              onClick={onExport}
              disabled={exporting}
              sx={{ py: 0.5, px: 1.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}
            >
              {exporting ? 'Exporting…' : filters.county !== 'All' ? `Export PDF · ${filters.county}` : 'Export PDF'}
            </Button>
          )}

          <USAJILIColorModeToggle color={TEXT_DIM} />

          {/* User avatar → profile menu */}
          <Avatar
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              cursor: 'pointer', width: 30, height: 30, fontSize: '0.75rem',
              fontWeight: 700, bgcolor: '#FF6B4A',
            }}
          >
            {initials}
          </Avatar>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <ListItemText
                primary={officer?.full_name}
                secondary={role}
                primaryTypographyProps={{ fontSize: '0.85rem' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={async () => {
                setMenuAnchor(null);
                await logout();
                navigate('/usajili/login', { replace: true });
              }}
            >
              <LogoutIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
              <Typography variant="body2">Sign out</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>

        {/* ── Navigation tabs — inside AppBar, matching SDICS admin ── */}
        <Box
          sx={{
            borderBottom: `1px solid ${APPBAR_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: 2,
            pr: 1.5,
          }}
        >
          <Tabs
            value={tabIdx}
            onChange={(_, v) => setTabIdx(v)}
            variant="scrollable"
            scrollButtons={false}
            TabIndicatorProps={{ sx: { display: 'none' } }}
            sx={{
              minHeight: 36,
              '& .MuiTabs-flexContainer': { gap: 0.25, px: 0.5 },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 500,
                color: TEXT_DIM,
                py: 0.75,
                px: 1.5,
                minWidth: 'auto',
                minHeight: 36,
                borderRadius: 0.75,
                mx: 0.25,
                my: 0.5,
                transition: 'all 0.15s ease',
                '&.Mui-selected': {
                  color: ACCENT_GREEN,
                  backgroundColor: alpha(ACCENT_GREEN, 0.14),
                  fontWeight: 600,
                },
                '&:hover': {
                  backgroundColor: alpha(TEXT_MAIN, 0.06),
                  color: TEXT_MAIN,
                },
              },
            }}
          >
            {TABS.map((t) => (
              <Tab
                key={t.label}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    {t.icon}
                    <span>{t.label}</span>
                  </Box>
                }
              />
            ))}
          </Tabs>
          <Box sx={{ ml: 'auto', py: 0.75, pl: 1.5, width: { xs: '100%', sm: 'auto' } }}>
            <USAJILIFilterBar />
          </Box>
        </Box>
      </AppBar>

      {/* ── Main content ── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: { lg: 'auto' },
          backgroundColor: CONTENT_BG,
          p: { xs: 1, sm: 1.5 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {exportError && (
          <Alert severity="error" onClose={() => setExportError('')} sx={{ mb: 1.5 }}>{exportError}</Alert>
        )}
        {tabIdx === 0 && <USAJILIOverviewTab />}
        {tabIdx === 1 && <USAJILIPivotTab />}
        {tabIdx === 2 && (
          <USAJILIDailyTab openAdd={addRequested} onAddOpened={() => setAddRequested(false)} />
        )}
      </Box>

      {isAdmin && (
        <>
          <USAJILIBulkUploadDialog
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            onDone={bumpRefetch} // refresh the dashboard; the dialog stays open to show the result
          />
          <USAJILIAuditLogDialog open={auditOpen} onClose={() => setAuditOpen(false)} />
        </>
      )}
    </Box>
  );
};

export const USAJILIDashboardPage: React.FC = () => (
  <USAJILIThemeProvider>
    <USAJILIProvider>
      <Shell />
    </USAJILIProvider>
  </USAJILIThemeProvider>
);

export default USAJILIDashboardPage;
